/**
 * ExternalSystemCard (P3-C) — display 1 system với sync status + actions.
 */
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  FileText,
  Github,
  Globe,
  Loader2,
  PenLine,
  RefreshCw,
  Trash2,
} from "lucide-react";

import type { ExternalSystem } from "@/api/externalSystems";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const TYPE_META: Record<
  ExternalSystem["type"],
  { Icon: typeof Github; label: string; color: string }
> = {
  git: { Icon: Github, label: "Git", color: "text-violet-600" },
  url: { Icon: Globe, label: "URL", color: "text-blue-600" },
  database: { Icon: Database, label: "Database", color: "text-emerald-600" },
  rest_api: { Icon: Globe, label: "REST API", color: "text-amber-600" },
};

interface Props {
  system: ExternalSystem;
  busy?: boolean;
  onSync: () => void;
  onViewDoc: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export function ExternalSystemCard({
  system,
  busy = false,
  onSync,
  onViewDoc,
  onEdit,
  onDelete,
}: Props): JSX.Element {
  const meta = TYPE_META[system.type];
  const Icon = meta.Icon;
  const isSyncing = system.sync_status === "syncing" || busy;
  const isDone = system.sync_status === "done";
  const isError = system.sync_status === "error";

  const charCount = (system.doc_metadata.char_count as number | undefined) ?? null;

  return (
    <Card
      className={cn(
        "transition-all",
        isDone && "border-emerald-300/60 dark:border-emerald-700/40",
        isError && "border-rose-300/60 dark:border-rose-700/40",
      )}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className={cn("size-9 rounded-md bg-muted flex items-center justify-center shrink-0", meta.color)}>
            <Icon className="size-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">{system.name}</div>
            <div className="flex items-center gap-1.5">
              <code className="text-[10px] text-muted-foreground">
                {system.slug}
              </code>
              <Badge variant="outline" className="text-[9px] py-0 px-1.5 h-4">
                {meta.label}
              </Badge>
            </div>
            {system.description ? (
              <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">
                {system.description}
              </p>
            ) : null}
          </div>
        </div>

        {/* Config preview */}
        <div className="text-[11px] text-muted-foreground space-y-0.5">
          {system.type === "git" ? (
            <code className="block truncate">
              {String(system.config.url ?? "(no url)")}
            </code>
          ) : system.type === "url" ? (
            <code className="block truncate">
              {String(system.config.url ?? "")}
            </code>
          ) : system.type === "database" ? (
            <code className="block truncate">
              {maskDsn(String(system.config.dsn ?? ""))}
            </code>
          ) : null}
        </div>

        {/* Status */}
        <div className="flex items-center gap-2 min-h-[24px] flex-wrap">
          {isDone ? (
            <Badge className="bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] px-1.5 py-0">
              <CheckCircle2 className="size-3 mr-0.5" />
              Synced
              {charCount ? ` · ${charCount.toLocaleString()} chars` : ""}
            </Badge>
          ) : isError ? (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
              <AlertTriangle className="size-3 mr-0.5" />
              Lỗi
            </Badge>
          ) : isSyncing ? (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              <Loader2 className="size-3 mr-0.5 animate-spin" />
              Đang sync...
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              Chưa sync
            </Badge>
          )}
          {system.last_synced_at ? (
            <span className="text-[10px] text-muted-foreground">
              {new Date(system.last_synced_at).toLocaleString("vi-VN")}
            </span>
          ) : null}
        </div>

        {/* Error detail */}
        {isError && system.last_error ? (
          <p className="text-[10px] text-destructive bg-destructive/5 border border-destructive/20 rounded px-2 py-1 line-clamp-3">
            {system.last_error}
          </p>
        ) : null}

        {/* Actions */}
        <div className="flex gap-1 pt-1">
          <Button
            size="sm"
            variant={isDone ? "outline" : "brand"}
            className="flex-1"
            onClick={onSync}
            disabled={isSyncing}
          >
            {isSyncing ? (
              <>
                <Loader2 className="size-3.5 mr-1 animate-spin" />
                Đang sync...
              </>
            ) : isDone ? (
              <>
                <RefreshCw className="size-3.5 mr-1" />
                Re-sync
              </>
            ) : (
              <>
                <RefreshCw className="size-3.5 mr-1" />
                Sync
              </>
            )}
          </Button>
          {system.has_doc ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={onViewDoc}
              title="Xem doc"
            >
              <FileText className="size-3.5" />
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={onEdit} title="Sửa">
            <PenLine className="size-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground hover:text-destructive"
            onClick={onDelete}
            title="Xoá"
            disabled={busy}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Mask password trong DSN: postgresql://user:pass@host → postgresql://user:***@host */
function maskDsn(dsn: string): string {
  return dsn.replace(/(:\/\/[^:]+):[^@]+@/, "$1:***@");
}
